import { Pagination } from "./Pagination";
import { SearchField } from "./SearchField";

interface EntityOption {
  id: string;
  name: string;
}

interface EntityMultiSelectProps<T extends EntityOption> {
  label: string;
  searchLabel: string;
  searchPlaceholder: string;
  requirement?: string;
  hint?: string;
  selectedIds: string[];
  options: T[] | null;
  emptyMessage: string;
  nameFilter: string;
  onNameFilterChange: (value: string) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onToggle: (option: T) => void;
  renderLabel?: (option: T) => React.ReactNode;
  selections?: Record<string, T>;
  filter?: React.ReactNode;
}

export function EntityMultiSelect<T extends EntityOption>({
  label,
  searchLabel,
  searchPlaceholder,
  requirement,
  hint,
  selectedIds,
  options,
  emptyMessage,
  nameFilter,
  onNameFilterChange,
  page,
  totalPages,
  onPageChange,
  onToggle,
  renderLabel,
  selections,
  filter,
}: EntityMultiSelectProps<T>) {
  // A selection stays visible even when the filter or the current page would
  // otherwise hide it, but order never depends on what is selected: a badge
  // that moved when you clicked it would cost you your place in the list.
  const selected = selectedIds
    .map((id) => selections?.[id])
    .filter((option): option is T => option !== undefined);

  const byId = new Map<string, T>();

  for (const option of [...selected, ...(options ?? [])]) {
    byId.set(option.id, option);
  }

  const visible = [...byId.values()].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return (
    <fieldset className="min-w-0">
      <legend className="mb-1.5 block text-sm text-foreground">
        {label}{" "}
        {requirement && (
          <span className="text-foreground/70">({requirement})</span>
        )}{" "}
        <span className="text-xs text-foreground/70">
          {selectedIds.length} selected
        </span>
      </legend>

      <div className="rounded-lg border border-muted bg-base p-3">
        <div className="mb-2 flex gap-2">
          <SearchField
            value={nameFilter}
            onChange={onNameFilterChange}
            label={searchLabel}
            placeholder={searchPlaceholder}
          />
          {filter}
        </div>

        {options === null ? (
          <ul className="flex list-none flex-wrap gap-2 p-0">
            {["w-16", "w-24", "w-20"].map((width) => (
              <li key={width}>
                <span
                  role="status"
                  aria-label="Loading"
                  className={`block h-[30px] ${width} animate-pulse rounded-full bg-surface`}
                />
              </li>
            ))}
          </ul>
        ) : visible.length === 0 ? (
          <p className="text-sm text-foreground/70">{emptyMessage}</p>
        ) : (
          <ul className="flex list-none flex-wrap gap-2 p-0">
            {visible.map((option) => {
              const isSelected = selectedIds.includes(option.id);

              return (
                <li key={option.id}>
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onToggle(option)}
                    className={`rounded-full border px-3 py-1 text-sm ${
                      isSelected
                        ? "border-accent text-accent"
                        : "border-muted text-foreground hover:border-accent"
                    }`}
                  >
                    {renderLabel ? renderLabel(option) : option.name}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {hint && <p className="mt-2 text-xs text-foreground/70">{hint}</p>}

        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    </fieldset>
  );
}
